import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Sales from './components/Sales';
import Clients from './components/Clients';
import ChannelPartners from './components/ChannelPartners';
import Media from './components/Media';
import Walkins from './components/Walkins';
import Followups from './components/Followups';
import VDNB from './components/VDNB';
import Reports from './components/Reports';
import Inventory from './components/Inventory';
import SalesStrategy from './components/SalesStrategy';
import LaunchWarRoom from './components/LaunchWarRoom';
import Collaterals from './components/Collaterals';
import CostSheet from './components/CostSheet';
import Chatbot from './components/Chatbot';
import BrokerMarketing from './components/BrokerMarketing';
import SaledriveMeta from './components/SaledriveMeta';
import AutomatedCaller from './components/AutomatedCaller';
import WhatsAppIntimation from './components/WhatsAppIntimation';
import Admin from './components/Admin';
import ClientDetails from './components/ClientDetails';
import ConstructionMilestones from './components/ConstructionMilestones';
import MicrositeDomains from './components/MicrositeDomains';
import Projects from './components/Projects';
import NPVCalculator from './components/NPVCalculator';
import Intelligence from './components/IntelligenceHub';
import EOIList from './components/EOIList';
import Overrides from './components/Overrides';
import BookingBoard from './components/BookingBoard';
import SocialMedia from './components/SocialMedia';
import EOI from './components/EOI';
import WhatsAppTemplates from './components/WhatsAppTemplates';
import AccessControl from './components/AccessControl';
import MasterData from './components/MasterData';
import ChatbotPage from "./components/ChatbotPage";
import ActivityReport from "./components/ActivityReport";
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import './App.css';
import { TenantProvider } from './contexts/TenantContext';

const API_BASE = '/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set page title from tenant name
  useEffect(() => {
    const tenantName = localStorage.getItem("tenantName");
    if (tenantName) document.title = tenantName + " - Dashboard";
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "true");

  useEffect(() => {
    // Check for saved token and user
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Set default auth header for axios if you have global config, 
      // otherwise components should attach it or use an interceptor.
      // For now, we assume components or an interceptor handle it, 
      // but let's ensure we are consistent.
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Save tenant data from login response (if available)
    if (userData.tenant) {
      localStorage.setItem('tenant', JSON.stringify(userData.tenant));
      localStorage.setItem("tenantName", userData.tenant?.name || "Dashboard");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    setUser(null);
    window.location.href = '/dashboard'; // Force refresh to clear any state
  };

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleToggleCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", next);
      return next;
    });
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Protected Route Component
  const ProtectedRoute = ({ children, allowedRoles, requiredPermission }) => {
    if (!user) return <Navigate to="/" replace />;

    // 1. Admin always has access
    if (user.role === 'admin' || user.role === 'super_admin' || user.role === 'superadmin') return children;

    // 2. Check Dynamic Permission Override (if permission specified)
    if (requiredPermission && user.permissions && Array.isArray(user.permissions)) {
      const override = user.permissions.find(p => p.module === requiredPermission);
      if (override) {
        // If specific override exists (either explicit allow or deny)
        if (override.can_read === 1) return children;
        return <div className="p-4 text-center">Access denied by administrator.</div>;
      }
    }

    // 3. Fallback to Role Check
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <div className="p-4 text-center">You do not have permission to view this page.</div>;
    }

    return children;
  };

  if (loading) return <div className="loading">Loading...</div>;

  if (!user) {
    return <TenantProvider apiBase={API_BASE}><Login onLogin={handleLogin} apiBase={API_BASE} /></TenantProvider>;
  }

  return (
    <TenantProvider apiBase={API_BASE}>
    <Router basename="/dashboard">
      <div className="app">
        {sidebarOpen && <div className="sidebar-overlay active" onClick={handleSidebarClose} />}
        <Sidebar user={user} onLogout={handleLogout} isOpen={sidebarOpen} onClose={handleSidebarClose} collapsed={sidebarCollapsed} onToggleCollapse={handleToggleCollapse} />
        <div className={"main-content" + (sidebarCollapsed ? " collapsed" : "")}>
          <Header user={user} onMenuToggle={handleMenuToggle} />
          <div className="content">
            <Routes>
              {/* Common Routes */}
              <Route path="/" element={<Dashboard apiBase={API_BASE} user={user} />} />
              <Route path="/clients/:id" element={<ClientDetails apiBase={API_BASE} />} />
              <Route path="/inventory" element={<Inventory apiBase={API_BASE} user={user} />} />
              <Route path="/booking-board" element={<ProtectedRoute allowedRoles={['sales', 'sales_head', 'admin']} requiredPermission="inventory"><BookingBoard apiBase={API_BASE} user={user} /></ProtectedRoute>} />
              <Route path="/cost-sheet" element={<CostSheet apiBase={API_BASE} />} />
              <Route path="/chatbot" element={<ChatbotPage apiBase={API_BASE} />} />
              <Route path="/activity-report" element={<ActivityReport apiBase={API_BASE} />} />
              <Route path="/followups" element={<Followups apiBase={API_BASE} />} />
              <Route path="/walkins" element={<Walkins apiBase={API_BASE} />} />

              {/* Sales Routes */}
              <Route path="/clients" element={<ProtectedRoute allowedRoles={['sales', 'sales_head', 'admin']} requiredPermission="clients"><Clients apiBase={API_BASE} user={user} /></ProtectedRoute>} />

              {/* Admin/Manager Routes */}
              <Route path="/sales-strategy" element={
                <ProtectedRoute allowedRoles={['admin', 'sales_head', 'manager']} requiredPermission="sales_strategy">
                  <SalesStrategy apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/launch-war-room" element={
                <ProtectedRoute allowedRoles={['admin', 'sales_head', 'manager']} requiredPermission="launch_war_room">
                  <LaunchWarRoom apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['admin']} requiredPermission="admin">
                  <Admin apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute allowedRoles={['admin']} requiredPermission="admin">
                  <Projects apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/master-data" element={<ProtectedRoute allowedRoles={["admin"]} requiredPermission="admin"><MasterData apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/channel-partners" element={
                <ProtectedRoute allowedRoles={['admin', 'sourcing', 'sales_head', 'sales', 'sales_coordinator']} requiredPermission="channel_partners">
                  <ChannelPartners apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="reports">
                  <Reports apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/access-control" element={
                <ProtectedRoute allowedRoles={['admin']} requiredPermission="access_control">
                  <AccessControl apiBase={API_BASE} />
                </ProtectedRoute>
              } />
              <Route path="/whatsapp-templates" element={
                <ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="whatsapp_intimation">
                  <WhatsAppTemplates />
                </ProtectedRoute>
              } />

              {/* Other Routes */}
              <Route path="/sales" element={<ProtectedRoute allowedRoles={['sales', 'sales_head', 'admin']} requiredPermission="sales"><Sales apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/media" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="media"><Media apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/vdnb" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="vdnb"><VDNB apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/collaterals" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="collaterals"><Collaterals apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/broker-marketing" element={<ProtectedRoute allowedRoles={['admin']} requiredPermission="broker_marketing"><BrokerMarketing apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/construction-milestones" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="construction_milestones"><ConstructionMilestones apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/microsite-domains" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="microsite_domains"><MicrositeDomains apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/npv-calculator" element={<ProtectedRoute allowedRoles={['sales', 'admin']} requiredPermission="sales"><NPVCalculator apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/intelligence" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="intelligence"><Intelligence apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/social-media" element={<ProtectedRoute allowedRoles={['admin', 'sales_head']} requiredPermission="social_media"><SocialMedia apiBase={API_BASE} /></ProtectedRoute>} />
              <Route path="/overrides" element={<Overrides apiBase={API_BASE} user={user} />} />
              <Route path="/eoi" element={<ProtectedRoute allowedRoles={['sales', 'admin']} requiredPermission="cost_sheet"><EOIList apiBase={API_BASE} /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
      </div>
      <Chatbot apiBase={API_BASE} />
    </Router>
    </TenantProvider>
  );
}

export default App;

